import { useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import { SUPPORTED_CURRENCIES, getCurrencyName, getCurrencySymbol } from "@/lib/currency";

/**
 * Radix `Select` rejects `value=""`, so the optional "use the default"
 * item carries this internal value and is mapped back to '' on change.
 */
const DEFAULT_SENTINEL = "_default";

/** The currency's symbol, or '' when the runtime has no glyph and echoes the code. */
function distinctSymbol(code: string): string {
  const symbol = getCurrencySymbol(code);
  return symbol === code ? "" : symbol;
}

export interface CurrencySelectorProps {
  /** Selected ISO 4217 code. '' is allowed only when `defaultOptionLabel` is set. */
  value: string;
  /** Receives the chosen code, or '' when the default option is chosen. */
  onValueChange: (code: string) => void;
  /** Codes to offer. Defaults to every supported currency (`SUPPORTED_CURRENCIES`). */
  options?: readonly string[];
  /**
   * Label for a leading "no explicit currency" item (e.g. "Organization
   * default (EUR)"). When set, choosing it emits ''.
   */
  defaultOptionLabel?: string;
  /** Forwarded to the trigger so a `<Label htmlFor>` can target it. */
  id?: string;
  disabled?: boolean;
  /** Extra classes for the trigger (width, etc.). */
  className?: string;
  hasError?: boolean;
}

/**
 * The one currency picker. Codes come from `SUPPORTED_CURRENCIES` (or the
 * `options` subset), symbols and localized names from `Intl` via
 * `@/lib/currency` — there is no symbol or name table anywhere.
 *
 * Items read `€  EUR · Euro`; the trigger shows the compact `€ EUR` so the
 * picker fits narrow slots (e.g. beside an amount input).
 *
 * @example
 * // Every supported currency
 * <CurrencySelector value={currency} onValueChange={setCurrency} />
 *
 * // Curated org-base list
 * <CurrencySelector options={ORG_BASE_CURRENCIES} value={…} onValueChange={…} />
 *
 * // With an "inherit" option that maps to ''
 * <CurrencySelector defaultOptionLabel="Organization default (EUR)" value={field.value} onValueChange={field.onChange} />
 */
export function CurrencySelector({
  value,
  onValueChange,
  options = SUPPORTED_CURRENCIES,
  defaultOptionLabel,
  id,
  disabled = false,
  className,
  hasError = false,
}: CurrencySelectorProps) {
  const { t, i18n } = useTranslation();
  // The symbol/name wrappers read the language from the i18next singleton
  // themselves; this is only the memo key. Optional so a key-echo test mock
  // (`{ t }` without `i18n`) still renders.
  const language = i18n?.language;
  const code = value.trim().toUpperCase();
  const hasDefaultOption = defaultOptionLabel !== undefined;

  // Symbols and names are Intl lookups; compute once per option set + language.
  const items = useMemo(
    () =>
      options.map((option) => ({
        code: option,
        // Some currencies have no glyph and Intl returns the code itself
        // (CHF); leave the symbol column empty rather than print "CHF CHF".
        symbol: distinctSymbol(option),
        name: getCurrencyName(option),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is the cache key for the Intl lookups
    [options, language],
  );

  const selectValue = code || (hasDefaultOption ? DEFAULT_SENTINEL : "");

  return (
    <Select
      value={selectValue}
      onValueChange={(next) => onValueChange(next === DEFAULT_SENTINEL ? "" : next)}
      disabled={disabled}
    >
      <SelectTrigger id={id} className={cn("w-full", hasError && "border-destructive", className)}>
        <SelectValue placeholder={t("currency.select")}>
          {code ? [distinctSymbol(code), code].filter(Boolean).join(" ") : defaultOptionLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {hasDefaultOption && <SelectItem value={DEFAULT_SENTINEL}>{defaultOptionLabel}</SelectItem>}
        {items.map((item) => (
          <SelectItem key={item.code} value={item.code}>
            <span className="flex items-center gap-2">
              <span className="w-6 text-muted-foreground">{item.symbol}</span>
              <span>{item.code}</span>
              <span className="text-muted-foreground">· {item.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
